/**
 * What the assistant may do to a kitchen, and nothing else.
 *
 * "Add more drawers by the cooktop", "make the left wall 3.2 metres", "swap the doors for
 * drawers" -- a person says it in words, the model turns it into calls on THESE tools, and
 * the app carries them out with the same operations a finger uses. The model never edits the
 * plan directly, so it cannot produce a kitchen the maker will not build: every refusal the
 * app already makes ("that wall is full") comes back to it as a tool error, and it explains
 * rather than pretends.
 *
 * One file, two readers. The SERVER sends `TOOLS` to the model, so their schemas live here;
 * the CLIENT runs `execute` against the live scene, so the mapping lives here too. Pure ESM,
 * no Vue, no three.js, so Node can import it.
 */

/** Strict schemas: `additionalProperties: false`, everything named in `required`. */
const strict = (properties, required = Object.keys(properties)) => ({
  type: 'object', properties, required, additionalProperties: false,
})

export const TOOLS = [
  {
    name: 'describe_kitchen',
    description: 'The kitchen as it stands: every wall, every run with its cabinets in order (with ids, widths and roles), the finishes, and what the designer review says. Call this first.',
    strict: true,
    input_schema: strict({}),
  },
  {
    name: 'list_range',
    description: "The cabinets this maker sells, with ids, kinds (base, wall = overhead, tall), widths and what they are for. Only ids from here may be used.",
    strict: true,
    input_schema: strict({ kind: { type: 'string', enum: ['base', 'wall', 'tall', 'any'] } }),
  },
  {
    name: 'add_cabinet',
    description: 'Put a cabinet from the range on a wall. The neighbours make room within what the maker will cut; if the wall is full this fails and says so. Optionally beside a role (sink, cooktop, fridge, dishwasher, oven) or at an end.',
    strict: true,
    input_schema: strict({
      specId: { type: 'string' },
      wallId: { type: 'string', description: 'A wall id from describe_kitchen. Empty for the first wall of the right kind.' },
      near: { type: 'string', enum: ['sink', 'cooktop', 'fridge', 'dishwasher', 'oven', 'start', 'end', 'anywhere'] },
    }),
  },
  {
    name: 'remove_cabinet',
    description: 'Take a cabinet out. Its width goes to its neighbours, or becomes a filler.',
    strict: true,
    input_schema: strict({ itemId: { type: 'string' } }),
  },
  {
    name: 'swap_cabinet',
    description: 'Change one cabinet into another from the range, keeping its place and as much of its width as the new one allows. Must be the same kind.',
    strict: true,
    input_schema: strict({ itemId: { type: 'string' }, specId: { type: 'string' } }),
  },
  {
    name: 'set_width',
    description: 'Make a cabinet a different width, within the range the maker will cut it at. Neighbours absorb the difference.',
    strict: true,
    input_schema: strict({ itemId: { type: 'string' }, mm: { type: 'integer' } }),
  },
  {
    name: 'move_cabinet',
    description: 'Move a cabinet to a position in the order of a wall (0 = first from the wall start), on the same wall or round a corner onto a joined one.',
    strict: true,
    input_schema: strict({ itemId: { type: 'string' }, wallId: { type: 'string' }, index: { type: 'integer' } }),
  },
  {
    name: 'set_wall_length',
    description: 'Make a wall a different length, in millimetres. Cabinets on it re-cut themselves; what was arranged stays where it was.',
    strict: true,
    input_schema: strict({ wallId: { type: 'string' }, mm: { type: 'integer' } }),
  },
  {
    name: 'set_shape',
    description: 'Which walls carry cabinets: one wall, galley (two facing), L, or U.',
    strict: true,
    input_schema: strict({ shape: { type: 'string', enum: ['single', 'galley', 'l', 'u'] } }),
  },
  {
    name: 'toggle_wall',
    description: 'Put cabinets on a wall that has none, or take them all off a wall that has.',
    strict: true,
    input_schema: strict({ wallId: { type: 'string' } }),
  },
  {
    name: 'set_finish',
    description: 'Choose a door style, colour, benchtop, splashback or handle by its id from describe_kitchen.',
    strict: true,
    input_schema: strict({
      field: { type: 'string', enum: ['doorStyleId', 'colourId', 'benchtopId', 'splashbackId', 'handleId'] },
      optionId: { type: 'string' },
    }),
  },
  {
    name: 'lock',
    description: 'Keep a sink, cooktop, oven, fridge or dishwasher where it is through drags and redesigns, or let it move again.',
    strict: true,
    input_schema: strict({ itemId: { type: 'string' }, locked: { type: 'boolean' } }),
  },
  {
    name: 'design_options',
    description: 'Ask the generator for three kitchens for this room, keeping whatever is locked. Returns their names, reasons and ids; apply one with choose_option.',
    strict: true,
    input_schema: strict({}),
  },
  {
    name: 'choose_option',
    description: 'Apply one of the kitchens from design_options by its id.',
    strict: true,
    input_schema: strict({ optionId: { type: 'string' } }),
  },
]

/**
 * A compact picture of the kitchen for the model. Ids are what it will act on; widths are
 * what it will reason about; roles are what it will look for.
 */
export function describe(plan, maker, review, schedule) {
  const spec = (id) => maker.cabinets.find((c) => c.id === id)
  return {
    walls: plan.walls.map((w) => ({ id: w.id, mm: Math.round(Math.hypot(w.x2 - w.x1, w.y2 - w.y1)) })),
    runs: plan.runs.map((r) => ({
      id: r.id, wallId: r.wallId, kind: r.kind,
      usableMm: Math.round((r.to ?? 0) - (r.from ?? 0)),
      cabinets: r.items.map((it, i) => ({
        index: i, itemId: it.id, specId: it.gap ? 'gap' : it.specId,
        name: it.gap ? '(nothing: over a tall unit)' : it.filler ? 'filler' : spec(it.specId)?.name ?? it.specId,
        mm: Math.round(it.w), role: spec(it.specId)?.role ?? null,
        drawers: spec(it.specId)?.drawers ?? 0,
        locked: (plan.locks ?? []).some((l) => l.itemId === it.id),
      })),
    })),
    finishes: {
      doorStyleId: plan.doorStyleId, colourId: plan.colourId, benchtopId: plan.benchtopId,
      splashbackId: plan.splashbackId, handleId: plan.handleId,
    },
    finishOptions: {
      doorStyleId: maker.doorStyles.map((x) => ({ id: x.id, name: x.name })),
      colourId: maker.colours.map((x) => ({ id: x.id, name: x.name })),
      benchtopId: maker.benchtops.map((x) => ({ id: x.id, name: x.name })),
      splashbackId: maker.splashbacks.map((x) => ({ id: x.id, name: x.name })),
      handleId: maker.handles.map((x) => ({ id: x.id, name: x.name })),
    },
    summary: schedule ? { cabinets: schedule.counts.cabinets, benchtopMetres: schedule.benchtopMetres } : null,
    review: (review ?? []).map((f) => ({ level: f.level, code: f.code, itemId: f.itemId ?? null, text: f.text })),
  }
}

/** The maker's range, for `list_range`. */
export function range(maker, kind = 'any') {
  return maker.cabinets
    .filter((c) => kind === 'any' || c.kind === kind)
    .map((c) => ({
      specId: c.id, name: c.name, kind: c.kind, mm: c.w,
      cutFrom: c.stretch[0], cutTo: c.stretch[1],
      drawers: c.drawers, doors: c.doors, role: c.role ?? null,
    }))
}

/**
 * The system prompt, from the maker's own numbers. Stable across a conversation, so it
 * caches; the kitchen itself is described through a tool call, not here.
 */
export function systemPrompt(maker, RULE) {
  return [
    `You help a homeowner arrange a kitchen that ${maker.name} will build. You change the kitchen ONLY through the tools; you never describe a change you did not make with a tool.`,
    'Start every turn by calling describe_kitchen, because the kitchen changes between turns.',
    'Use only cabinet ids from list_range and item ids from describe_kitchen. If the range has no cabinet for what is asked, say so and offer the nearest thing in the range.',
    `The maker will cut a cabinet only within its cutFrom–cutTo range and builds nothing wider than ${maker.limits.maxCabinetWidth}mm. A wall is always exactly full: adding takes width from neighbours, removing gives it back. If a tool fails, say why in the maker's terms and suggest what would make it fit.`,
    `Designer's rules the review applies: ${RULE.cooktopSide}mm of bench either side of a cooktop and never in a corner; ${RULE.sinkSideMajor}/${RULE.sinkSideMinor}mm either side of a sink; dishwasher within ${RULE.dishwasherToSink}mm of the sink; a rangehood, not a cupboard, above the cooktop; a corner unit where two runs meet; ${RULE.walkway}mm between facing runs.`,
    'When done, answer in one or two plain sentences: what changed, and anything the review now says must be fixed. No lists, no headings, no offers of further help.',
  ].join('\n')
}
